import { MAX_DESCRIPTION_LENGTH } from './item';
import { isReservedEvent, isReservedObjectAttribute, isReservedProfileAttribute } from './reserved-descriptions';
import type { DataIndexItemKind } from './types';

/**
 * The rules a row is edited under: how long a description may be, and which
 * rows the workspace may write at all.
 */

/**
 * Why a description cannot be saved, or nothing when it can. The message
 * says "less than", which is a character out from the limit it enforces —
 * the copy is the page this replaces, and changing it is a copy decision
 * rather than a porting one.
 */
export function descriptionError(description: string): string | undefined {
  return description.length > MAX_DESCRIPTION_LENGTH
    ? `Description must be less than ${MAX_DESCRIPTION_LENGTH} characters`
    : undefined;
}

/** How much of the limit is used, shown while there is still room. */
export function descriptionCounter(description: string): string {
  return `${description.length}/${MAX_DESCRIPTION_LENGTH}`;
}

/**
 * Whether a row can be written from the *list*, in place.
 *
 * The stricter of the two rules: a reserved name arrives with Customer.io's
 * own description, and the list will not let it be typed over in a cell.
 * Which names are reserved depends on the kind: `email` means something on a
 * person and nothing in particular on an object. The drawer and the item
 * page are looser — see `isEditableInDrawer` — and that disagreement is the
 * page this replaces, not ours (`name-cell/index.gts:92-107` against
 * `item-details/usage/index.gts:126-139`).
 *
 * Not the same question as whether a row can be selected: a bulk action also
 * refuses an attribute the workspace has promoted to an identifier, which is
 * about deleting it rather than about describing it.
 */
export function isEditableByWorkspace(kind: DataIndexItemKind, name: string): boolean {
  switch (kind) {
    case 'event':
      return !isReservedEvent(name);
    case 'object-attribute':
    case 'object-relationship-attribute':
      return !isReservedObjectAttribute(name);
    case 'attribute':
      return !isReservedProfileAttribute(name);
    default: {
      const _exhaustive: never = kind;
      throw new Error(`unhandled data index item kind: ${String(_exhaustive)}`);
    }
  }
}

/**
 * Whether the drawer's Edit is offered, which is a looser question.
 *
 * A reserved name is Customer.io's, but the description, tags and
 * sensitivity beside it are still the workspace's — so the page this
 * replaces heads its drawer with Edit on a reserved *attribute* and refuses
 * only a reserved event (`item-details/usage/index.gts:126-139`, whose
 * comment says reserved status affects deletion and not editing). Its own
 * item page agrees, and so does ours. The list's inline cells are the
 * stricter surface and keep `isEditableByWorkspace`
 * (`data-index-list-table/name-cell/index.gts:92-103`).
 */
export function isEditableInDrawer(kind: DataIndexItemKind, name: string): boolean {
  return kind === 'event' ? !isReservedEvent(name) : true;
}
